/**
 * Elementy „Historia zmian" (audit log) PER KARTOTEKA — `contractor_audit_view`,
 * `employee_audit_view`, `company_audit_view`.
 *
 * Decyzja (WF NEW 2026-08-21): audyt ma być przenośnym ELEMENTEM per kartoteka (jak
 * adresy: `contractor_addresses_view` vs `employee_addresses_view`), a NIE jednym wspólnym
 * `audit_log_view`. Dzięki temu każda kartoteka ma własny klocek „Historia zmian" w swojej
 * palecie (allowedModules zawężone) — usuwalny z zakładki i dodawalny z powrotem („nic nie
 * ginie"). Read-side pozostaje per kartoteka (ContractorAuditTab / SystemModuleAuditTab) —
 * dispatch po TYPIE elementu na stronie detalu (mirror wzorca adresów: osobny typ, wspólny/
 * własny read-side). Stary wspólny `audit_log_view` zostaje jako fallback dla layoutów, które
 * jeszcze go trzymają (dispatch na tych samych stronach) — patrz `AuditLogViewElement`.
 *
 * `render` wołany jak funkcja — bez hooków w ciele (i18n.t standalone). Podgląd w canvasie;
 * realna historia dochodzi przez contextRenderers strony detalu.
 */

import { History } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';

function makeAuditElement(type: string, moduleSlug: string): LayoutElementDefinition {
  return {
    type,
    category: 'tresc',
    isContainer: false,
    label: 'Historia zmian',
    icon: 'History',
    allowedModules: [moduleSlug],
    defaultSettings: () => ({}),
    render: ({ element }) => (
      <div
        className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
        data-testid={`${type}-element-${element.id}`}
        data-element-type={type}
      >
        <History className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
        <p className="mt-1 text-xs font-medium text-text-secondary">
          {i18n.t('tenant.systemModuleAudit.paletteLabel', { defaultValue: 'Historia zmian' })}
        </p>
      </div>
    ),
  };
}

export const ContractorAuditViewElement = makeAuditElement('contractor_audit_view', 'contractors');
export const EmployeeAuditViewElement = makeAuditElement('employee_audit_view', 'employees');
export const CompanyAuditViewElement = makeAuditElement('company_audit_view', 'companies');
export const UserAuditViewElement = makeAuditElement('user_audit_view', 'users');
export const ContractorContactAuditViewElement = makeAuditElement(
  'contractor_contact_audit_view',
  'contractor_contacts',
);
// dokument-blokify (2026-08-23): Faktury zakupu — PoC parytetu z kartotekami dla
// obszaru Dokumenty. Pozostałe 6 klas dokumentów (sale_invoices/contracts/letters/
// commercial/internal/specjalne) dopisane niżej tym samym przepisem.
export const PurchaseInvoiceAuditViewElement = makeAuditElement(
  'purchase_invoice_audit_view',
  'purchase_invoices',
);
// dokument-blokify (2026-08-24): Faktury sprzedaży — druga klasa dokumentu wg tego
// samego przepisu (patrz purchase_invoices wyżej). Kolejne 5 (contracts/letters/
// commercial/internal/specjalne) dopisane niżej.
export const SaleInvoiceAuditViewElement = makeAuditElement(
  'sale_invoice_audit_view',
  'sale_invoices',
);
// dokument-blokify (2026-08-24): Umowy — trzecia klasa dokumentu, ten sam przepis.
export const ContractAuditViewElement = makeAuditElement('contract_audit_view', 'contracts');
// dokument-blokify (2026-08-24): Pisma — czwarta klasa dokumentu, ten sam przepis.
export const LetterAuditViewElement = makeAuditElement('letter_audit_view', 'letters');
// dokument-blokify (2026-08-24): Dokument handlowy — piąta klasa dokumentu, ten sam przepis.
export const CommercialAuditViewElement = makeAuditElement('commercial_audit_view', 'commercial');
// dokument-blokify (2026-08-24): Dokument wewnętrzny — szósta (ostatnia) klasa, ten sam przepis.
export const InternalAuditViewElement = makeAuditElement('internal_audit_view', 'internal');
// kartoteka-blokify (2026-08-27): Towary — „Historia zmian" jako przenośny blok per
// kartoteka (ten sam przepis co kontrahenci/faktury). Wcześniej Audit log był
// wstrzykiwany na sztywno przez top-up (ProductDetail pageTabs), więc usunięcie z
// buildera nie było trwałe. Read-side = wspólny SystemModuleAuditTab (moduleSlug).
export const ProductAuditViewElement = makeAuditElement('product_audit_view', 'products');
// dokument-blokify: Dokumenty specjalne — siódma (ostatnia) klasa dokumentów, ten sam
// przepis co commercial/internal wyżej.
export const SpecjalneAuditViewElement = makeAuditElement('specjalne_audit_view', 'specjalne');
// kartoteka Zadania: „Historia zmian" jako przenośny blok (ten sam przepis co Towary);
// read-side = wspólny SystemModuleAuditTab (moduleSlug='tasks').
export const TaskAuditViewElement = makeAuditElement('task_audit_view', 'tasks');
